import Container from '@/components/Container';
import Link from '@/components/Link';
import Image from '@/components/Image';
import RemoteImage from '@/components/Image/RemoteImage';
import HeaderSub from '@/view/HeaderSub';
import client from '@/api/client';
import { partnerQuery, partnerSlugsQuery } from '@/api/queries';

export async function getStaticProps({ params }) {
  const { data } = await client().query({
    query: partnerQuery,
    variables: {
      id: params.slug,
    },
  });

  return {
    props: {
      partner: data?.partner,
    }
  }
}

export async function getStaticPaths() {
  const { data } = await client().query({ query: partnerSlugsQuery });

  const paths = data?.partners?.nodes?.map(({ slug }) => ({
    params: { slug },
  }));

  return {
    paths,
    fallback: false,
  };
}

export default function Partner({
  partner,
}) {
  const hasIntro = partner.info.intro !== null;
  const hasWebsite = partner.info.website !== null;
  const hasAbout = partner.content !== null;
  const hasServices = false;
  const hasTestimonials = false;
  const hasDetails = hasAbout || hasServices || hasTestimonials;

  const finalHeading = partner.info.heading || partner.title;

  return (
    <div className="bg-white">
      <div className="relative z-10 bg-gradient-to-t from-green-100 to-white">
        <HeaderSub
          pageTitle={(
            <Link  href="/socios" className="inline-flex items-center space-x-2 transition ease-in-out duration-100 text-green-500 hover:text-gray-900">
              <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              <div className="text-base font-semibold">
                Volver al listado de socios
              </div>
            </Link>
          )}  
        />
        <Container maxWidth="max-w-screen-lg" className="pt-4 pb-10 lg:pb-20 space-y-10 lg:space-y-20">
          <div className="flex flex-col items-center space-y-4">
            <div className="inline-flex items-center -space-x-2">
              <div className="w-28 h-28 rounded-full shadow-outside-lg relative z-20">
                <Image
                  className="block w-28 h-28 rounded-full"
                  src={partner.featuredImage.node.sourceUrl}
                  srcSet={partner.featuredImage.node.srcSet}
                  alt={partner.title}
                  width={120}
                  height={120}
                />
              </div>
              <div className="w-28 h-28 rounded-full shadow-outside-lg relative z-10">
                <RemoteImage
                  className="w-28 h-28 rounded-full"
                  src="v1612275132/socios/aibes_fntveq.png"
                  srcSet={partner.featuredImage.node.srcSet}
                  alt="Asociación Iberoamericana de Scrum"
                  width={120}
                  height={120}
                />
              </div>
            </div>
          </div>
          <div className="space-y-8">
            <h1 className="font-medium text-xl lg:text-4xl tracking-tight text-center mx-auto max-w-screen-md">
              <div className="leading-normal">
                {finalHeading}
              </div>
            </h1>
            {hasIntro && (
              <div className="grid grid-cols-1 lg:grid-cols-12">
                <div
                  className="prose lg:prose-xl font-normal text-gray-700 lg:col-span-10 lg:col-start-2 text-center"
                  dangerouslySetInnerHTML={{ __html: partner.info.intro }}
                />
              </div>
            )}
            {hasWebsite && (
              <div className="flex justify-center items-center">
                <a className="inline-flex rounded-full leading-6 px-7 py-3 bg-green-500 text-white font-semibold text-lg transition ease-in-out duration-100 hover:bg-green-600 focus:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-600" href={partner.info.website} target="_blank" rel="noopener noreferrer">
                  Certifícate hoy mismo
                </a>
              </div>
            )}
          </div>
        </Container>
      </div>
      {hasDetails && (
        <div className="relative z-20 lg:sticky lg:top-0 bg-white shadow-sm">
          <Container>
            <nav className="flex items-center justify-center space-x-6">
              {hasAbout && (
                <a className="font-medium text-lg flex py-4 leading-10" href="#acerca-de">
                  Acerca de
                </a>
              )}
              {hasServices && (
                <a className="font-medium text-lg flex py-4 leading-10" href="#servicios">
                  Servicios
                </a>
              )}
              {hasTestimonials && (
                <a className="font-medium text-lg flex py-4 leading-10" href="#testimonios">
                  Testimonios
                </a>
              )}
            </nav>
          </Container>
        </div>
      )}
      <div className="bg-gray-100 relative z-10">
        <Container>
          {hasAbout && (
            <div id="acerca-de" className="max-w-screen-md mx-auto py-10 lg:py-20">
              <div
                className="prose lg:prose-lg font-normal text-gray-900"
                dangerouslySetInnerHTML={{ __html: partner.content }}
              />
            </div>
          )}
        </Container>
      </div>
    </div>
  );
}

Partner.layoutProps = {
  stickyHeader: false,
};
